// Runtime app config (public/config.json) and access to the config feature service (SPEC 4).
import type { ArcGISIdentityManager } from "@esri/arcgis-rest-request";
import { request } from "@esri/arcgis-rest-request";
import { searchItems, getItem, updateItem, searchGroups, createGroup, shareItemWithGroup } from "@esri/arcgis-rest-portal";
import { createFeatureService, addToServiceDefinition, queryFeatures, applyEdits } from "@esri/arcgis-rest-feature-service";
import type { Me } from "../auth";

export interface AppConfig {
  portalUrl: string;
  appId: string;
  configItemTag: string;   // "orgfilter-config"
  soeAssetPath: string;    // "assets/GICHD_OrgFilter.soe"
}

export const CONFIG_TYPE_KEYWORD = "orgfilter-config-v1";
export const CONFIG_SERVICE_NAME = "OrgFilter_Config";
export const ADMIN_GROUP_TITLE = "OrgFilter Administrators";

export async function loadAppConfig(): Promise<AppConfig> {
  const res = await fetch("config.json", { cache: "no-store" });
  if (!res.ok) throw new Error("config.json missing next to index.html (copy config.sample.json)");
  return (await res.json()) as AppConfig;
}

/** Rows of the four tables, SPEC 4.1 - 4.4. */
export interface PolicyRow { objectid?: number; service_key: string; server_url: string; service_path: string; enabled: 0 | 1; version: number; policy_json: string; updated_by: string; updated_at: number; notes?: string; }
export interface UserOrgRow { objectid?: number; username: string; org: string; region: string | null; updated_by: string; updated_at: number; }
export interface AuditRow { objectid?: number; at: number; actor: string; action: string; service_key: string | null; detail_json: string; }
export interface StatusRow { objectid?: number; service_key: string; machine: string; soi_version: string; policy_version: number | null; policy_state: string; last_refresh: number; last_error: string | null; started_at: number; }

export type TableName = "policies" | "user_orgs" | "audit" | "status";

export class ConfigServiceMissingError extends Error {
  constructor() { super(`No ${CONFIG_SERVICE_NAME} service found in this Portal. Run Setup first.`); }
}

// ---------------------------------------------------------------------------
// Table definitions (SPEC 4.1 - 4.4). Non-spatial tables in a hosted service.
// ---------------------------------------------------------------------------

type FieldSpec = [name: string, type: "string" | "integer" | "smallInteger" | "date", length?: number];

const TABLES: Record<TableName, FieldSpec[]> = {
  policies: [
    ["service_key", "string", 512],
    ["server_url", "string", 256],
    ["service_path", "string", 256],
    ["enabled", "smallInteger"],
    ["version", "integer"],
    ["policy_json", "string", 1_000_000],
    ["updated_by", "string", 128],
    ["updated_at", "date"],
    ["notes", "string", 2000]
  ],
  user_orgs: [
    ["username", "string", 256],
    ["org", "string", 64],
    ["region", "string", 64],
    ["updated_by", "string", 128],
    ["updated_at", "date"]
  ],
  audit: [
    ["at", "date"],
    ["actor", "string", 128],
    ["action", "string", 64],
    ["service_key", "string", 512],
    ["detail_json", "string", 100_000]
  ],
  status: [
    ["service_key", "string", 512],
    ["machine", "string", 128],
    ["soi_version", "string", 32],
    ["policy_version", "integer"],
    ["policy_state", "string", 32],
    ["last_refresh", "date"],
    ["last_error", "string", 2000],
    ["started_at", "date"]
  ]
};

function esriType(t: FieldSpec[1]): string {
  return { string: "esriFieldTypeString", integer: "esriFieldTypeInteger", smallInteger: "esriFieldTypeSmallInteger", date: "esriFieldTypeDate" }[t];
}

function tableDefinition(name: TableName): Record<string, unknown> {
  return {
    name,
    type: "Table",
    displayField: TABLES[name][0][0],
    description: `GICHD Org Filter ${name} (SPEC 4)`,
    objectIdField: "objectid",
    hasAttachments: false,
    supportsAdvancedQueries: true,
    fields: [
      { name: "objectid", type: "esriFieldTypeOID", alias: "OBJECTID", nullable: false, editable: false },
      ...TABLES[name].map(([fieldName, type, length]) => ({
        name: fieldName,
        type: esriType(type),
        alias: fieldName,
        nullable: true,
        editable: true,
        ...(length ? { length } : {})
      }))
    ],
    indexes: [],
    templates: [{ name: "New", description: "", prototype: { attributes: {} } }],
    types: []
  };
}

// ---------------------------------------------------------------------------

interface Located { itemId: string; serviceUrl: string; }

export class ConfigService {
  private located: Located | null = null;
  private tableUrls: Partial<Record<TableName, string>> = {};

  constructor(
    private readonly session: ArcGISIdentityManager,
    private readonly cfg: AppConfig,
    private readonly me: Me
  ) {}

  /** Finds the OrgFilter_Config item by tag/typeKeyword; null when bootstrap is needed (SPEC 8.4). */
  async locate(): Promise<Located | null> {
    if (this.located) return this.located;
    const found = await searchItems({
      q: `tags:"${this.cfg.configItemTag}" AND typekeywords:"${CONFIG_TYPE_KEYWORD}" AND type:"Feature Service"`,
      authentication: this.session,
      num: 10
    });
    const item = found.results.find((r) => !!r.url);
    if (!item) return null;
    this.located = { itemId: item.id, serviceUrl: item.url!.replace(/\/+$/, "") };
    return this.located;
  }

  /** Bootstrap: createService + addToDefinition with the SPEC 4 tables, tags, sharing. */
  async bootstrap(onProgress: (step: string) => void = () => {}): Promise<Located> {
    const existing = await this.locate();
    if (existing) return existing;

    onProgress(`Creating hosted feature service ${CONFIG_SERVICE_NAME}`);
    const created = await createFeatureService({
      authentication: this.session,
      item: {
        name: CONFIG_SERVICE_NAME,
        serviceDescription: "GICHD Org Filter: policies, user lookup, audit and SOI status. Managed by the Org Filter Manager.",
        hasStaticData: false,
        maxRecordCount: 10000,
        allowGeometryUpdates: false,
        // Editing but no Extract and no Sync: the policies must not leave the Portal (SPEC 8.4).
        capabilities: "Query,Create,Update,Delete,Editing",
        xssPreventionInfo: { xssPreventionEnabled: true, xssPreventionRule: "InputOnly", xssInputRule: "rejectInvalid" }
      }
    });
    const serviceUrl = (created.encodedServiceURL ?? created.serviceurl ?? "").replace(/\/+$/, "");
    const itemId = created.itemId;
    if (!serviceUrl || !itemId) throw new Error("The Portal created the service but returned no URL or item id");

    onProgress("Adding the policies, user_orgs, audit and status tables");
    await addToServiceDefinition(serviceUrl, {
      authentication: this.session,
      tables: (Object.keys(TABLES) as TableName[]).map(tableDefinition)
    });

    onProgress("Tagging the item so the Manager can find it again");
    const item = await getItem(itemId, { authentication: this.session });
    await updateItem({
      authentication: this.session,
      item: {
        id: itemId,
        title: CONFIG_SERVICE_NAME,
        snippet: "GICHD Org Filter configuration",
        description: "Created by the Org Filter Manager. Do not edit these tables by hand; see docs/SPEC.md section 4.",
        tags: unique([...(item.tags ?? []), this.cfg.configItemTag]),
        typeKeywords: unique([...(item.typeKeywords ?? []), CONFIG_TYPE_KEYWORD])
      }
    });

    onProgress(`Sharing with the ${ADMIN_GROUP_TITLE} group`);
    const groupId = await this.ensureAdminGroup();
    await shareItemWithGroup({ id: itemId, groupId, authentication: this.session, owner: this.me.username });

    this.located = { itemId, serviceUrl };
    this.tableUrls = {};
    await this.audit({ action: "bootstrap", service_key: null, detail_json: JSON.stringify({ itemId, serviceUrl, groupId }) });
    return this.located;
  }

  /** The group holding the administrators and the SOI service account (SPEC 4). */
  async ensureAdminGroup(): Promise<string> {
    const found = await searchGroups({ q: `title:"${ADMIN_GROUP_TITLE}"`, authentication: this.session, num: 10 });
    const hit = found.results.find((g) => g.title === ADMIN_GROUP_TITLE);
    if (hit) return hit.id;
    const created = await createGroup({
      authentication: this.session,
      group: {
        title: ADMIN_GROUP_TITLE,
        access: "org",
        tags: ["orgfilter"],
        snippet: "Members may read the Org Filter configuration. Includes the SOI service account.",
        isInvitationOnly: true,
        sortField: "title",
        sortOrder: "asc"
      }
    });
    return created.group.id;
  }

  async requireLocated(): Promise<Located> {
    const located = await this.locate();
    if (!located) throw new ConfigServiceMissingError();
    return located;
  }

  /** Table urls are resolved by name, never by assumed layer id. */
  async tableUrl(name: TableName): Promise<string> {
    const cached = this.tableUrls[name];
    if (cached) return cached;
    const { serviceUrl } = await this.requireLocated();
    // No "?f=json" in the URL: request() appends its own query string, and a URL that already
    // has one ends up with two, which the server rejects with a bare HTTP 400.
    const def = await request(serviceUrl, { authentication: this.session, httpMethod: "GET", params: { f: "json" } });
    const all = [...(def.tables ?? []), ...(def.layers ?? [])] as Array<{ id: number; name: string }>;
    for (const t of all) {
      if ((Object.keys(TABLES) as string[]).includes(t.name)) this.tableUrls[t.name as TableName] = `${serviceUrl}/${t.id}`;
    }
    const url = this.tableUrls[name];
    if (!url) throw new Error(`The ${CONFIG_SERVICE_NAME} service has no "${name}" table. It may have been created by an older version.`);
    return url;
  }

  private async query<T>(name: TableName, where: string, orderByFields?: string): Promise<T[]> {
    const url = await this.tableUrl(name);
    const res = await queryFeatures({
      url,
      where,
      outFields: ["*"],
      returnGeometry: false,
      orderByFields,
      authentication: this.session
    });
    return ((res as { features?: Array<{ attributes: T }> }).features ?? []).map((f) => f.attributes);
  }

  // --- policies (SPEC 4.1) ------------------------------------------------

  async getPolicy(serviceKey: string): Promise<PolicyRow | null> {
    const rows = await this.query<PolicyRow>("policies", `service_key = '${sql(serviceKey)}'`);
    return rows[0] ?? null;
  }

  async listPolicies(): Promise<PolicyRow[]> {
    return this.query<PolicyRow>("policies", "1=1", "service_key ASC");
  }

  /**
   * Bumps `version` so every SOI instance notices on its next refresh, and records
   * the change in `audit` with a before/after diff (SPEC 8.7).
   */
  async savePolicy(row: PolicyRow, previous: PolicyRow | null): Promise<PolicyRow> {
    const url = await this.tableUrl("policies");
    const next: PolicyRow = {
      ...row,
      policy_json: escapeAngleBrackets(row.policy_json),
      version: (previous?.version ?? 0) + 1,
      updated_by: this.me.username,
      updated_at: Date.now()
    };
    const edits = previous?.objectid
      ? { updates: [{ attributes: { ...next, objectid: previous.objectid } }] }
      : { adds: [{ attributes: stripOid(next) }] };
    const res = await applyEdits({ url, authentication: this.session, ...edits });
    const result = (res.addResults?.[0] ?? res.updateResults?.[0]) as { objectId?: number; success?: boolean; error?: { description?: string } } | undefined;
    if (!result?.success) throw new Error(`Saving the policy failed: ${result?.error?.description ?? "unknown error"}`);
    next.objectid = previous?.objectid ?? result.objectId;

    await this.audit({
      action: previous ? "policy.update" : "policy.create",
      service_key: next.service_key,
      detail_json: JSON.stringify({
        version: { from: previous?.version ?? null, to: next.version },
        enabled: { from: previous?.enabled ?? null, to: next.enabled },
        before: previous ? safeParse(previous.policy_json) : null,
        after: safeParse(next.policy_json)
      })
    });
    return next;
  }

  async setPolicyEnabled(row: PolicyRow, enabled: boolean): Promise<void> {
    const url = await this.tableUrl("policies");
    await applyEdits({
      url,
      authentication: this.session,
      updates: [{ attributes: { objectid: row.objectid, enabled: enabled ? 1 : 0, version: row.version + 1, updated_by: this.me.username, updated_at: Date.now() } }]
    });
    await this.audit({ action: enabled ? "policy.enable" : "policy.disable", service_key: row.service_key, detail_json: JSON.stringify({ version: row.version + 1 }) });
  }

  async deletePolicy(row: PolicyRow): Promise<void> {
    const url = await this.tableUrl("policies");
    await applyEdits({ url, authentication: this.session, deletes: [row.objectid!] });
    await this.audit({ action: "policy.delete", service_key: row.service_key, detail_json: JSON.stringify({ policy: safeParse(row.policy_json) }) });
  }

  // --- user_orgs (SPEC 4.2) -----------------------------------------------

  async listUserOrgs(): Promise<UserOrgRow[]> {
    return this.query<UserOrgRow>("user_orgs", "1=1", "username ASC");
  }

  /** The SOI matches case-insensitively, so the Manager looks rows up the same way. */
  async findUserOrg(username: string): Promise<UserOrgRow | null> {
    const rows = await this.query<UserOrgRow>("user_orgs", `UPPER(username) = '${sql(username.toUpperCase())}'`);
    return rows[0] ?? null;
  }

  /** Insert or update by username; org is stored upper-case, region lower-case (SPEC 4.2). */
  async upsertUserOrgs(rows: UserOrgRow[]): Promise<void> {
    if (rows.length === 0) return;
    const url = await this.tableUrl("user_orgs");
    const existing = new Map((await this.listUserOrgs()).map((r) => [r.username.toUpperCase(), r]));
    const adds: Array<{ attributes: UserOrgRow }> = [];
    const updates: Array<{ attributes: UserOrgRow }> = [];
    for (const row of rows) {
      const normalised: UserOrgRow = {
        username: row.username.trim(),
        org: row.org.trim().toUpperCase(),
        region: row.region?.trim().toLowerCase() || null,
        updated_by: this.me.username,
        updated_at: Date.now()
      };
      const prior = existing.get(normalised.username.toUpperCase());
      if (prior) updates.push({ attributes: { ...normalised, objectid: prior.objectid } });
      else adds.push({ attributes: normalised });
    }
    await applyEdits({ url, authentication: this.session, adds, updates });
    for (const a of adds) await this.audit({ action: "userorg.add", service_key: null, detail_json: JSON.stringify(a.attributes) });
    for (const u of updates) await this.audit({ action: "userorg.update", service_key: null, detail_json: JSON.stringify(u.attributes) });
  }

  async deleteUserOrgs(rows: UserOrgRow[]): Promise<void> {
    if (rows.length === 0) return;
    const url = await this.tableUrl("user_orgs");
    await applyEdits({ url, authentication: this.session, deletes: rows.map((r) => r.objectid!) });
    for (const r of rows) await this.audit({ action: "userorg.delete", service_key: null, detail_json: JSON.stringify({ username: r.username, org: r.org, region: r.region }) });
  }

  // --- audit (SPEC 4.3) ---------------------------------------------------

  /** Append-only. A failed audit write must never hide the action it describes. */
  async audit(entry: Omit<AuditRow, "objectid" | "at" | "actor">): Promise<void> {
    try {
      const url = await this.tableUrl("audit");
      await applyEdits({
        url,
        authentication: this.session,
        // detail_json embeds the policy, so it carries the same angle brackets policy_json does.
        adds: [{
          attributes: {
            ...entry,
            detail_json: escapeAngleBrackets(entry.detail_json ?? ""),
            at: Date.now(),
            actor: this.me.username
          }
        }]
      });
    } catch (e) {
      console.warn("Org Filter: audit entry could not be written", entry.action, e);
    }
  }

  async listAudit(filter?: { serviceKey?: string; since?: number; action?: string }): Promise<AuditRow[]> {
    const clauses: string[] = [];
    if (filter?.serviceKey) clauses.push(`service_key = '${sql(filter.serviceKey)}'`);
    if (filter?.action) clauses.push(`action = '${sql(filter.action)}'`);
    if (filter?.since) clauses.push(`at >= ${sqlTimestamp(filter.since)}`);
    return this.query<AuditRow>("audit", clauses.length ? clauses.join(" AND ") : "1=1", "at DESC");
  }

  // --- status (SPEC 4.4) --------------------------------------------------

  async listStatus(serviceKey?: string): Promise<StatusRow[]> {
    return this.query<StatusRow>("status", serviceKey ? `service_key = '${sql(serviceKey)}'` : "1=1", "service_key ASC, machine ASC");
  }
}

/**
 * A policy document always contains angle brackets, because SPEC 5.2 requires named capture
 * groups and those are written `(?<org>…)`. A hosted feature service created with XSS prevention
 * set to `rejectInvalid` refuses any value containing `<`, so the config service would reject the
 * very documents it exists to hold.
 *
 * Escaping them as `\u003c` / `\u003e` keeps the stored text free of literal brackets while
 * remaining the same JSON: both `JSON.parse` and the SDK's `JSONObject` decode the escapes back to
 * the original characters. The alternative — turning XSS prevention off on the service — weakens a
 * control that costs us nothing to keep.
 */
export const escapeAngleBrackets = (json: string): string =>
  json.replace(/</g, "\\u003c").replace(/>/g, "\\u003e");

/**
 * A date field cannot be compared against a raw epoch number in an ArcGIS where clause -- the
 * service answers "Invalid data type for expression". It needs a standard SQL timestamp literal,
 * in UTC, which is how the values were written.
 */
function sqlTimestamp(epochMs: number): string {
  const iso = new Date(epochMs).toISOString();       // 2026-10-01T13:37:42.513Z
  return `TIMESTAMP '${iso.slice(0, 10)} ${iso.slice(11, 19)}'`;
}

/** Single quotes doubled: these values reach a SQL where clause. */
const sql = (v: string): string => v.replace(/'/g, "''");
const unique = (xs: string[]): string[] => [...new Set(xs)];
const stripOid = <T extends { objectid?: number }>(row: T): Omit<T, "objectid"> => { const { objectid, ...rest } = row; return rest; };

function safeParse(json: string): unknown {
  try { return JSON.parse(json); } catch { return json; }
}
